/**
 * AboutPage — MADE-303
 *
 * Company story for offshore-sails.com.
 * Contains the single approved "Doyle" credit line — the ONLY occurrence of
 * "Doyle" in the entire marketing codebase.
 */

import React from 'react';
import { Box, Button, Container, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MarketingLayout } from './MarketingLayout';

export const AboutPage: React.FC = () => {
  return (
    <MarketingLayout pageTitle="About Us">

      {/* ── Page header ────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#002680', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography
            component="h1"
            variant="h3"
            sx={{ color: 'white', fontWeight: 700, mb: 1.5 }}
          >
            About Offshore Sails
          </Typography>
          <Box sx={{ width: 60, height: 4, bgcolor: '#FFC726', mx: 'auto', mt: 2 }} />
        </Container>
      </Box>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="md">
          <Stack spacing={6}>

            {/* Origin and location */}
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{ color: '#002680', fontWeight: 700, mb: 2.5 }}
              >
                Caribbean-Born. Offshore-Proven.
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85, mb: 2 }}>
                Offshore Sails is headquartered in Barbados, where we are the largest
                sailmaking facility in the Americas. Our sails ship and travel worldwide,
                serving customers across the Americas, Europe, and beyond. Our location shapes
                everything about how we build sails: the cloth weights we specify, the
                UV-protection treatments we apply, the reinforcing constructions we insist on,
                and the warranties we stand behind.
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85 }}>
                When a sail leaves our loft, it has been designed, cut, and assembled by people
                who understand what serious offshore conditions ask of a sail, and who have
                been answering that question for nearly four decades.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(0,38,128,0.10)' }} />

            {/* Unique position */}
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{ color: '#002680', fontWeight: 700, mb: 2.5 }}
              >
                A Distinction That Matters
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85, mb: 2 }}>
                We are one of the only sailmakers in the world building Dacron sails in luff
                lengths up to 125 feet, and we specialize in sailboats over 55 feet, including
                charter and trade vessels. This reflects decades of accumulated knowledge about
                how large working sails behave under load, how Dacron cloth performs under
                sustained UV exposure, and how to engineer a sail that holds its shape and its
                stitching long after lesser products have failed.
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85 }}>
                Large-boat sailmaking at this level demands precision that cannot be rushed.
                Every sail is cut to exacting specifications, assembled with attention to every
                panel join, and finished with the same rigour regardless of whether it is going
                on a charter yacht or a private offshore passage-maker.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(0,38,128,0.10)' }} />

            {/* Craft and approach */}
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{ color: '#002680', fontWeight: 700, mb: 2.5 }}
              >
                Built for Where You Sail
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85, mb: 2 }}>
                Sails built for tropical latitudes and demanding offshore passages face a
                problem many lofts rarely have to solve: sustained ultraviolet intensity that breaks down cloth,
                thread, and coatings far faster than in milder climates. Our construction
                methods, including our Harris Paints UV-protection sealant, Sunbrella acrylic
                sunshields, and double-bonded edge tapes on our top-tier sails, reflect
                hard-won solutions to that challenge, applied to every sail we build.
              </Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85 }}>
                We build sails to last. Our flagship Offshore sail carries a five-year, 50,000-
                nautical-mile warranty on both workmanship and materials, a guarantee that
                reflects genuine confidence in how we build, not a marketing number.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(0,38,128,0.10)' }} />

            {/* Doyle credit — ONLY approved use of "Doyle" in this codebase */}
            <Box
              data-testid="doyle-credit"
              sx={{
                bgcolor: '#fafbff',
                border: '1px solid rgba(0,38,128,0.10)',
                borderLeft: '4px solid #FFC726',
                borderRadius: 2,
                p: { xs: 3, md: 4 },
              }}
            >
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.85 }}>
                Offshore Sails is the manufacturer of choice for Doyle sails in the Caribbean.
              </Typography>
            </Box>

          </Stack>

          {/* CTA */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={8} justifyContent="center">
            <Button
              component={RouterLink}
              to="/os/sails"
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#002680',
                color: 'white',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: '#001a60' },
              }}
            >
              View Our Sail Range
            </Button>
            <Button
              component={RouterLink}
              to="/os/contact"
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#FFC726',
                color: '#002680',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255,199,38,0.08)', borderColor: '#e6a800' },
              }}
            >
              Get in Touch
            </Button>
          </Stack>
        </Container>
      </Box>

    </MarketingLayout>
  );
};
